import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import './OpalAdvancedCallModal.css';
import TauriImage from '../TauriImage/TauriImage';
import { addRecord, getAll, getNextId, getRecordById, updateRecord } from '../../localDB';
import TauriLoader2 from '../TauriLoader2/TauriLoader2';
import appURL from '../../appURL.json';
import { useNavigate } from 'react-router-dom';
import { CallPhase, SessionTag, sessionType, statusMapping, SyncStatusMap } from '../../visualizations/visualizations';
import { useBaseProvider } from '../../BaseManager/BaseManager';

const OpalAdvancedCallModal = ({ show, onHide, session, onDone }) => {
    const navigate = useNavigate();

    const [customer, setSelectedCustomer] = useState(null);
    const [reasonList, setReasonList] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [timeIn, setTimeIn] = useState(false);
    const [timeOut, setTimeOut] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [userLocation, setUserLocation] = useState(null);

    const { setIsSidebarCollapsed } = useBaseProvider();

    const isOnline = JSON.parse(localStorage.getItem("isOnline"));
    const hasInternet = navigator.onLine;

    const getLocation = async () => {
        return new Promise((resolve, reject) => {

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    let address = null;

                    if (isOnline && hasInternet) {
                        try {
                            const token = localStorage.getItem("token");
                            const response = await fetch(`${appURL.API_GetLocationAddress}?lat=${latitude}&long=${longitude}`, {
                                method: "GET",
                                headers: {
                                    "Content-Type": "application/json",
                                    "Authorization": `Bearer ${token}`,
                                }
                            });

                            const data = await response.json();

                            address = data;
                        } catch (err) {
                            console.error("Reverse geocoding failed:", err);
                        }
                    }
                    setUserLocation({ latitude, longitude, address })
                    resolve({ latitude, longitude, address });
                },
                (error) => {
                    console.error(error);
                    reject("Unable to retrieve location");
                },
                {
                    enableHighAccuracy: true,
                    timeout: 20000, // stop waiting forever
                    maximumAge: 0
                }
            );
        });
    };

    const CreateAdvancedCallSession = async () => {
        setLoading(true);
        try {
            const isOnline = JSON.parse(localStorage.getItem("isOnline"));
            const hasInternet = navigator.onLine;

            if (isOnline && hasInternet) {
                await OnlineStartSession();
            } else {
                await OfflineStartSession();
            }

        } catch (err) {
            console.error('Failed to create advanced call reference!');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        try {
            const data = await getRecordById("customer", session?.AccountID);
            const reasons = await getAll("AdvancedCallReasons");
            setReasonList(reasons);
            setSelectedCustomer(data);
        } catch (error) {
            console.log("error fetching!");
        }
    };

    useEffect(() => {
        const initialize = async () => {
            Reset();
            fetchData();
            await checkAttendance();
        };

        initialize();
    }, [session]);



    const Reset = () => {
        setSelectedReason(null);
        setNotes("");
    }

    const CreateNotes = async (message, sessionID) => {
        const SessionNoteID = await getNextId("sessionNotes");
        const today = new Date();
        const newNoteToSave = {
            ID: SessionNoteID,
            Content: message,
            ContentType: 1,
            ReminderDate: today.toISOString().split("T")[0],
            ReminderTime: today.toISOString().split("T")[1],
            CreatedDate: today.toISOString(),
            ReminderLocation: null,
            SessionID: sessionID,
            Phase: CallPhase.PreCall,
            SyncStatus: SyncStatusMap.create
        };

        await addRecord("sessionNotes", newNoteToSave);
    }

    const checkAttendance = async () => {
        try {
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);

            const attendance = await getAll("Attendance");
            const filtered = attendance.filter(item => item.SyncStatus !== SyncStatusMap.done);
            const todayAttendance = filtered.find((a) => {
                const timeInDate = new Date(a?.TimeIn);
                timeInDate.setHours(0, 0, 0, 0); // normalize

                return timeInDate.getTime() === todayDate.getTime();
            });

            setTimeIn(todayAttendance?.TimeIn ? true : false);
            setTimeOut(todayAttendance?.TimeOut ? true : false);

        } catch (error) {
            console.log("Error getting the attendance!", error);
        }
    };

    const handleSubmit = () => {
        let message = null;

        if (!selectedReason && notes.trim() === "") {
            message = "Reason and notes are required.";
        } else if (!selectedReason) {
            message = "Reason is required.";
        } else if (notes.trim() === "") {
            message = "Notes are required.";
        }

        setErrorMessage(message);

        if (message) return;
        CreateAdvancedCallSession();
    };

    const OnlineStartSession = async () => {
        try {
            const sessionID = await getNextId("session");
            const sessionOrig = await getRecordById("session", session.ID);
            const { latitude, longitude, address } = await getLocation();

            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");

            const requestBody = {
                ID: sessionOrig.ID.toString(),
                VersionID: sessionOrig?.VersionID,
                Location: {
                    lat: latitude,
                    long: longitude
                },
                Advanced: {
                    ReasonID: selectedReason.ID,
                    Remarks: notes
                }
            };

            const response = await fetch(appURL.API_StartSession, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-DeviceID": devId
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                console.log("bad request");
                return
            }
            const data = await response.json();

            const date = new Date();
            const advancedCall = {
                ...sessionOrig,
                ID: sessionID,
                SessionDate: date.toLocaleDateString('en-CA'),
                ActualLocationLat: latitude,
                ActualLocationLong: longitude,
                ActualLocation: address ?? userLocation?.address,
                Status: statusMapping.pending,
                AdvancedCallReasonID: selectedReason.ID,
                AdvancedCallRemarks: notes,
                Type: sessionType.Advanced,
                Tag: SessionTag.advanced, //advanced,
                SyncStatus: SyncStatusMap.create
            }

            sessionOrig.AdvancedSessionID = sessionID;
            sessionOrig.AdvancedCallReasonID = selectedReason.ID;
            sessionOrig.AdvancedCallRemarks = notes;

            sessionOrig.ActualLocationLat = latitude;
            sessionOrig.ActualLocationLong = longitude;
            sessionOrig.ActualLocation = address ?? userLocation?.address;
            sessionOrig.ActualStart = data?.serverDate ?? new Date().toISOString();

            sessionOrig.Status = statusMapping.inprogress;

            await updateRecord('session', sessionOrig);
            await addRecord('session', advancedCall);

            const sessDate = new Date(sessionOrig.SessionDate);
            sessDate.setHours(0, 0, 0, 0);
            localStorage.setItem("dateSelect", JSON.stringify(sessDate));

            if (onDone) {
                onDone(true);
            }

            //to prevent the initial load to select the first item in sidebar 
            window.dispatchEvent(new Event("dateSelect"));

            // setIsSidebarCollapsed(true);
            localStorage.setItem("preventNavigation", true);
            setTimeout(() => {
                window.dispatchEvent(new Event("session"));
            }, 100);
            navigate(`/coverage/${session.ID}`);
            setIsSidebarCollapsed(true);
            onHide();

        } catch (error) {
            console.log(error);
        }
    };

    const OfflineStartSession = async () => {
        try {
            const sessionID = await getNextId("session");
            const sessionOrig = await getRecordById("session", session.ID);

            const { latitude, longitude, address } = await getLocation();

            const allItems = (await getAll("sessionItems")).filter(x => x.SyncStatus !== SyncStatusMap.done);
            const allNotes = (await getAll("sessionNotes")).filter(x => x.SyncStatus !== SyncStatusMap.done);
            const allItemInventories = (await getAll("SessionInventory")).filter(x => x.SyncStatus !== SyncStatusMap.done);
            const allInventoryImages = (await getAll("InvImages")).filter(x => x.SyncStatus !== SyncStatusMap.done);
            const allItemForRequests = (await getAll("SessionForRequest")).filter(x => x.SyncStatus !== SyncStatusMap.done);

            const sessionItems = allItems.filter((item) => item.SessionID === session.ID);
            const sessionNotes = allNotes.filter((item) => item.SessionID === session.ID);
            const sessionItemInventory = allItemInventories.filter((item) => item.SessionID === session.ID);
            const sessionInventoryImages = allInventoryImages.filter((item) => item.SessionID === session.ID);
            const sessionItemForRequests = allItemForRequests.filter((item) => item.SessionID === session.ID);

            const date = new Date();

            const advancedCall = {
                ...sessionOrig,
                ID: sessionID,
                SessionDate: date.toLocaleDateString('en-CA'),
                Status: statusMapping.pending,
                AdvancedCallReasonID: selectedReason.ID,
                AdvancedCallRemarks: notes,
                Type: sessionType.Advanced,
                Tag: SessionTag.advanced, //advanced,
                SyncStatus: SyncStatusMap.create
            }

            sessionOrig.AdvancedSessionID = sessionID;
            sessionOrig.AdvancedCallReasonID = selectedReason.ID;
            sessionOrig.AdvancedCallRemarks = notes;
            sessionOrig.SyncStatus = SyncStatusMap.update;

            sessionOrig.ActualLocationLat = latitude;
            sessionOrig.ActualLocationLong = longitude;
            sessionOrig.ActualLocation = address ?? userLocation?.address;
            sessionOrig.ActualStart = new Date().toISOString();

            sessionOrig.Status = statusMapping.inprogress;

            await CreateNotes(notes, sessionOrig.ID);
            await addRecord('session', advancedCall);
            await updateRecord('session', sessionOrig);

            // add Session items 

            const today = new Date().toISOString();
            for (const item of sessionItems) {
                const nextID = await getNextId("sessionItems");
                const newItem = {
                    ...item,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("sessionItems", newItem);
            }

            // Add Session Notes
            for (const note of sessionNotes) {
                const nextID = await getNextId("sessionNotes");
                const newNote = {
                    ...note,
                    ID: nextID, // unique ID for each
                    CreatedDate: today,
                    SessionID: sessionID,                 // reference to new session
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("sessionNotes", newNote);
            }

            // Add Session Item Inventory
            for (const inv of sessionItemInventory) {
                const nextID = await getNextId("SessionInventory");
                const newInv = {
                    ...inv,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("SessionInventory", newInv);
            }

            // Add Session Inventory Images
            for (const img of sessionInventoryImages) {
                const nextID = await getNextId("InvImages");
                const newImg = {
                    ...img,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("InvImages", newImg);
            }

            // Add Session Item For Requests
            for (const fr of sessionItemForRequests) {
                const nextID = await getNextId("SessionForRequest");
                const newFR = {
                    ...fr,
                    ID: nextID,
                    SessionID: sessionID,
                    SyncStatus: SyncStatusMap.create
                };
                await addRecord("SessionForRequest", newFR);
            }

            await CreateNotes(notes, sessionID);

            const sessDate = new Date(sessionOrig.SessionDate);
            sessDate.setHours(0, 0, 0, 0);
            localStorage.setItem("dateSelect", JSON.stringify(sessDate));

            if (onDone) {
                onDone(true);
            }

            //to prevent the initial load to select the first item in sidebar 
            window.dispatchEvent(new Event("dateSelect"));

            // setIsSidebarCollapsed(true);
            localStorage.setItem("preventNavigation", true);
            setTimeout(() => {
                window.dispatchEvent(new Event("session"));
            }, 100);
            navigate(`/coverage/${session.ID}`);
            setIsSidebarCollapsed(true);
            onHide();

        } catch (error) {
            return { error: "Network or system error." };
        }
    };

    return (
        <Modal
            className="opal-advancedcall-modal"
            show={show}
            onHide={onHide}
            centered
        >
            <Modal.Header className='advanced-header'>
                ADVANCED CALL
            </Modal.Header>
            <Modal.Body className='advanced-body'>
                <Form>
                    <Form.Group>
                        <Form.Label>NAME OF CONTACT</Form.Label>
                        <div className='name-contact'>
                            <span className='text-code'>{customer?.Code}</span>
                            <span>{customer?.Name}</span>
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-3 date-call">
                        <Form.Label>DATE OF CALL</Form.Label>
                        <div className="d-flex align-items-center justify-content-between date-display">
                            <Form.Control
                                plaintext
                                readOnly
                                value={new Date(session?.SessionDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                                className="flex-grow-1 p-0"
                            />
                            <TauriImage localImage={"/Images/f7--calendar-today.svg"} alt='icon' color={"#000"} />
                        </div>
                    </Form.Group>

                    <Form.Group className="mt-3 reason">
                        <Form.Label>REASON</Form.Label>
                        <Dropdown >
                            <Dropdown.Toggle className="form-control text-start">
                                {selectedReason
                                    ? (
                                        <span>
                                            <span className='text-code '>{selectedReason.Code}</span>
                                            <span>{selectedReason.Description}</span>
                                        </span>
                                    )
                                    : "-- Select Reason --"}
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="w-100">
                                {reasonList.map(reason => (
                                    <Dropdown.Item
                                        key={reason.Code}
                                        onClick={() => setSelectedReason(reason)}
                                    >
                                        <span className='text-code'>{reason.Code}</span>
                                        <span>{reason.Description}</span>
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </Form.Group>

                    <Form.Group className="mt-3 notes">
                        <Form.Label>NOTES</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Form.Group>
                    {errorMessage && (
                        <Form.Text className="text-danger d-flex align-items-center mt-2">
                            {errorMessage}
                        </Form.Text>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer className='advanced-footer'>
                <Button className="btn-advanced d-flex" onClick={handleSubmit} disabled={loading || (!timeIn || timeOut) || !session}>
                    {loading && (
                        <TauriLoader2 width="1.2rem" height="1.2rem" borderwidth="0.2rem" color="#FFF" />
                    )}
                    <span className="text-nowrap">Start Session</span>
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default OpalAdvancedCallModal;
